import React, { useContext, useMemo } from 'react'
import {
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  Platform,
} from 'react-native'
import Loader from './utility/loader'
import { Company, PopulatedValue } from '@ecountabl/lib'
// import { useSelector } from 'react-redux'
import Logo from './utility/logo'
import { ThemeContext } from './utility/context/ThemeContext'
// import DataCompleteness from './data-completeness'
// import Score from './score'
import { textStyles } from './style'
import { CompanyProfileRouteParams } from './company-profile'
import { useAppSelector } from './store'
import { Ionicons } from '@expo/vector-icons'
import Badge from './utility/badge'
import { StackActions, useNavigation } from '@react-navigation/native'
import Haptics from "./utility/haptics"
import { ValueBadge } from './utility/value-badge'
 
import CompanyViewFlatlist from './utility/companyViewFlatlist'
import Analytics from './utility/analytics'

type Props = {
  id: string;
  companies: Company[]
  showMore?: boolean
  onLoadMore?: () => void
  loadingMore?: boolean
  addlProfileParams?: CompanyProfileRouteParams
  /**
   * Disable the value-match icon badges overlay on each tile.
   * Badges are shown by default.
   */
  disableValuesBadges?: boolean
}

type SeeMoreProps = {
  onLoadMore: () => void
  loadingMore?: boolean
}

const SeeMoreAlts = ({ onLoadMore, loadingMore }: SeeMoreProps) => {
  const { theme } = useContext(ThemeContext)
  const viewStyles = [seeMoreStyles.button, {backgroundColor: theme.colors.disabled}]

  return <View style={styles.alternative}>
      {loadingMore ? (
        <View style={[viewStyles, { alignItems: 'center', justifyContent: 'center' }]}>
          <Loader />
        </View>
      ) : (
      <TouchableOpacity
        style={{flex: 1}}
        onPress={() => {
          Haptics.impactSoft()
          onLoadMore()
        }}
      >
        <View style={viewStyles}>
          <Text style={[textStyles.body, {textAlign: "center"}]}>See More</Text>
        </View>
      </TouchableOpacity>
    )}
  </View>
}

const seeMoreStyles = StyleSheet.create({
  button: {
    height: 110,
    width: 110,
    justifyContent: 'center',
    borderRadius: 5
  }
})

const HorizontalList: React.FC<Props> = ({
  id,
  companies,
  loadingMore,
  onLoadMore,
  showMore,
  addlProfileParams = {},
  disableValuesBadges = false,
}: Props) => {
  const { user } = useAppSelector(s => s.auth)
  const valuesMap = useAppSelector(s => s.values.map) as Record<string, PopulatedValue>
  const { theme } = useContext(ThemeContext)
  const { primary, white, textMain } = theme.colors
  const navigation = useNavigation()

  const shouldShowValuesBadges = !disableValuesBadges

  const LOGO_SIZE = 110
  const BADGE_SIZE = 24
  const BADGE_PADDING = 6
  const BADGE_AVAILABLE = LOGO_SIZE - (BADGE_PADDING * 2) - BADGE_SIZE

  const valueByLabel = useMemo(() => {
    const m = new Map<string, PopulatedValue>()
    try {
      Object.values(valuesMap || {}).forEach((v) => {
        if (v?.label) {
          m.set(v.label, v)
        }
      })
    } catch (_) { /* noop */ }
    return m
  }, [valuesMap])

  const userSelectedValueSlugs = useMemo(() => {
    const slugs = new Set<string>()
    try {
      const userMap = user?.valuedImpactAreaMap || {}
      Object.values(valuesMap || {}).forEach((v: any) => {
        const slug = typeof v?.slug === 'string' ? v.slug : undefined
        if (!slug) return
        const ias = Array.isArray(v?.impactAreas) ? v.impactAreas : []
        const anySelected = ias.some((ia: any) => !!userMap?.[ia?._id])
        if (anySelected) slugs.add(slug)
      })
    } catch (_) { /* noop */ }
    return slugs
  }, [user, valuesMap])

  const matchedValues = (co: Company): PopulatedValue[] => {
    if (!shouldShowValuesBadges) return []
    const aligned: any[] = Array.isArray(co?.valuesAligned) ? co.valuesAligned : []
    if (!aligned.length) return []

    const out: PopulatedValue[] = []
    const seen = new Set<string>()
    aligned.forEach((a) => {
      const slug = typeof a?.slug === 'string' ? a.slug : ''
      const label = typeof a?.label === 'string' ? a.label : ''
      if (!slug && !label) return
      // Only show badges for values the user has selected.
      if (userSelectedValueSlugs.size && slug && !userSelectedValueSlugs.has(slug)) return

      const v = (slug && valuesMap?.[slug]) ? valuesMap[slug] : valueByLabel.get(label)
      const key = v?.slug || slug || label
      if (!v || seen.has(key)) return
      seen.add(key)
      out.push(v)
    })
    return out
  }

  return (
    <CompanyViewFlatlist
      id={id}
      data={showMore ? [...companies, { _id: 'more' }] : companies}
      keyExtractor={(item) => item._id}
      contentContainerStyle={{ gap: 10 }}
      style={{ paddingTop: shouldShowValuesBadges ? 10 : 0 }}
      source={id}
      renderItem={({ item, index }) => {
        if (item._id === 'more') {
          return <SeeMoreAlts loadingMore={loadingMore} onLoadMore={onLoadMore} />
        }

        return (
          <View style={styles.alternative}>
            <TouchableOpacity
              activeOpacity={0.85}
              onPress={() => {
                Haptics.selection()
                Analytics.track('company_clicked', {
                  companyId: item._id,
                  companyName: item.name,
                  index,
                  source: "horizontal_list",
                })
                navigation.dispatch(
                  StackActions.push('Company', {
                    id: item._id,
                    index,
                    name: item.name,
                    isImpactNetworkBusiness: !!item.isImpactNetworkBusiness,
                    ...addlProfileParams
                  })
                )
              }}
            >
              <View style={styles.cardContainer}>
                <View style={styles.logoWrap}>
                  <Logo
                    company={item}
                    // for unknown crazytown reasons, trying to move this into a stylesheet
                    // will cause heights of images to expand 
                    style={{
                      width: 110,
                      height: 110,
                      padding: 5,
                      borderRadius: 5
                    }}
                  />
                  {shouldShowValuesBadges ? (
                    <View pointerEvents="none" style={styles.valueBadgesWrap}>
                      {(() => {
                        const badges = matchedValues(item)
                        const count = badges.length
                        return badges.map((v, idx) => {
                          // Spread evenly across tile width (no overflow).
                          // If there are many badges, they’ll overlap more but stay within bounds.
                          const left = count <= 1
                            ? (BADGE_PADDING + Math.round(BADGE_AVAILABLE / 2))
                            : (BADGE_PADDING + Math.round((BADGE_AVAILABLE * idx) / (count - 1)))

                          return (
                            <ValueBadge
                              key={v.slug || `${v.label}-${idx}`}
                              color={v.color}
                              accentFallbackColor={primary}
                              icon={v.icon}
                              label={v.label}
                              size={24}
                              iconSize={14}
                              style={{
                                position: "absolute",
                                left,
                                zIndex: 10 + idx,
                              }}
                            />
                          )
                        })
                      })()}
                    </View>
                  ) : null}
                  {
                    item.state && item.state == user.state ?
                    <Badge
                      icon={<Ionicons name="location-sharp" size={16} color={white}/>}
                      style={{
                        backgroundColor: primary,
                        position: "absolute",
                        bottom: -10,
                        right: -10,
                      }}
                    />
                    : null
                  }
                </View>
                <View style={styles.altTextWrap}>
                  <Text style={[textStyles.body, { textAlign: 'center', color: textMain }]}>
                    {item.name}
                  </Text>
                </View>
              </View>
            </TouchableOpacity>
          </View>
        )
      }}
    />
  )
}

export default HorizontalList

const styles = StyleSheet.create({
  hlist: {
    zIndex: 0,
    padding: 2,
    marginBottom: 20,
    paddingBottom: 7,
    overflow: 'visible'
  },
  label: {
    position: 'absolute',
    right: -12,
    top: 83
  },
  labelImageStyle: {
    opacity: 1
  },
  alternative: {
    width: 110,
    marginHorizontal: 6
  },
  cardContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
  },
  logoWrap: {
    position: 'relative',
    overflow: 'visible',
  },
  valueBadgesWrap: {
    position: 'absolute',
    top: -10,
    left: 0,
    right: 0,
    height: 24,
    overflow: 'visible',
    flexDirection: 'row',
    alignItems: 'center',
  },
  altTextWrap: {
    paddingHorizontal: 4,
    position: 'relative'
  },
  altText: {
    fontSize: 12,
    textAlign: 'center'
  },
  listLogo: {
    width: 100,
    height: 100,
    padding: 5,
  },
  highlight: {
    position: 'absolute',
    margin: -8,
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    borderWidth: 4,
    borderRadius: 4,
    zIndex: -1,
  },
  hoverable: {
    display: 'flex',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
})
